"use client";

import Link from "next/link";
import { AlertTriangle } from "lucide-react";
import { useTranslations } from "next-intl";
import { Skeleton } from "@/components/ui/skeleton";
import { formatPrice } from "@/lib/currency";
import { cn } from "@/lib/utils";
import type { OrderItem } from "../api";
import {
  getOrderAmountPaid,
  getOrderBalanceDue,
  getOrderItemsSummary,
  getOrderNumber,
  getOrderTotal,
} from "../partial-payment";

type Props = {
  order: OrderItem;
  locale: string;
  orderDetailUrl?: string;
  className?: string;
};

export function PartialPaymentBanner({
  order,
  locale,
  orderDetailUrl,
  className,
}: Props) {
  const t = useTranslations("partialPayment");
  const numberLocale = locale === "bn" ? "bn-BD" : "en-US";
  const orderNumber = getOrderNumber(order);
  const itemsSummary = getOrderItemsSummary(order, (title, count) =>
    t("itemsSummaryMore", { title, count }),
  );
  const amountPaid = formatPrice(getOrderAmountPaid(order), order.totals.currency, numberLocale);
  const total = formatPrice(getOrderTotal(order), order.totals.currency, numberLocale);
  const balanceDue = formatPrice(getOrderBalanceDue(order), order.totals.currency, numberLocale);

  return (
    <div
      role="status"
      aria-live="polite"
      className={cn(
        "flex flex-col gap-3 rounded-xl border border-orange-200 bg-orange-50 px-4 py-3 text-orange-950 sm:flex-row sm:items-start sm:justify-between",
        className,
      )}
    >
      <div className="flex min-w-0 gap-3">
        <div className="mt-0.5 flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-white text-primary shadow-sm">
          <AlertTriangle className="h-4 w-4" aria-hidden />
        </div>
        <p className="text-sm leading-6 text-orange-950/90">
          {t("bannerMessage", {
            amountPaid,
            total,
            itemsSummary,
            balanceDue,
            orderNumber,
          })}
        </p>
      </div>
      <Link
        href={orderDetailUrl ?? `/${locale}/dashboard/orders`}
        aria-label={t("viewOrderAria", { orderNumber })}
        className="inline-flex h-10 flex-shrink-0 items-center justify-center rounded-[10px] bg-primary px-4 text-sm font-semibold text-white transition-opacity hover:opacity-90 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
      >
        {t("viewOrder")}
      </Link>
    </div>
  );
}

export function PartialPaymentBannerSkeleton({ className }: { className?: string }) {
  return (
    <div
      className={cn(
        "rounded-xl border border-orange-100 bg-orange-50/70 px-4 py-3",
        className,
      )}
    >
      <div className="flex items-center gap-3">
        <Skeleton className="h-8 w-8 rounded-lg" />
        <div className="min-w-0 flex-1 space-y-2">
          <Skeleton className="h-3 w-full max-w-[720px]" />
          <Skeleton className="h-3 w-2/3 max-w-[520px]" />
        </div>
        <Skeleton className="hidden h-10 w-24 rounded-[10px] sm:block" />
      </div>
    </div>
  );
}

export function PartialPaymentFooterStrip({
  order,
  locale,
}: {
  order: OrderItem;
  locale: string;
}) {
  const t = useTranslations("partialPayment");
  const numberLocale = locale === "bn" ? "bn-BD" : "en-US";
  const balanceDue = formatPrice(getOrderBalanceDue(order), order.totals.currency, numberLocale);

  return (
    <div className="border-t border-orange-200 bg-orange-50 px-4 py-2 text-xs font-semibold text-orange-800">
      {t("libraryFooter", { balanceDue })}
    </div>
  );
}
